import { useMemo } from "react";
import { PaginationMetadata } from "@/interfaces/api";

type Options = Omit<PaginationMetadata, "total" | "limit"> & {
  totalVisible: number;
  onPageChange: (page: number) => void;
};

/**
 * Hook para construir e manipular a paginação de uma tabela.
 */
export const useBuildTablePagination = ({ page: currentPage, pageCount, totalVisible, onPageChange }: Options) => {
  const handlePageChange = (page: number | string) => {
    if (typeof page === "number" && page !== currentPage) {
      onPageChange(page);
    }
  };

  const createArray = (from: number, to: number) => {
    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
  };

  const pages = useMemo(() => {
    const amount = totalVisible ?? pageCount;

    if (amount === 0) {
      return [];
    }

    let maxLength = Math.min(totalVisible, pageCount);

    if (pageCount <= maxLength || pageCount === 5) {
      return createArray(1, pageCount);
    }

    if (maxLength < 5) {
      maxLength = 5;
    }

    if (currentPage < maxLength - 2) {
      return [...createArray(1, maxLength - 2), "...", pageCount];
    }

    if (currentPage > pageCount - maxLength + 3) {
      return [1, "...", ...createArray(pageCount - maxLength + 3, pageCount)];
    }

    const maxLengthCenter = maxLength - 4;

    const even = maxLengthCenter % 2 === 0;
    const range = even ? maxLengthCenter / 2 : (maxLengthCenter - 1) / 2;

    return [1, "...", ...createArray(currentPage - (even ? range - 1 : range), currentPage + range), "...", pageCount];
  }, [currentPage, pageCount, totalVisible]);

  return { pages, handlePageChange };
};
